import { DateTime } from "luxon";
import type {
  DataProvider,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  PaginationPayload,
  RaRecord,
  SortPayload,
} from "react-admin";
import { getAuthenticatedBackendClient, reactAdminToDRFOrdering, reactAdminToDRFPagination } from "../api/backend.api";
import {
  CreditNote as CreditNoteDto,
  type CreditNoteEndpoint,
  type CreditNoteStateEnum,
} from "../generated/backendClient";
import { AbstractDataProvider } from "./AbstractDataProvider";

export interface CreditNote extends RaRecord {
  id: string;
  nr: string;
  type: string;
  createdAt: DateTime;
  total?: number;
  status: CreditNoteStateEnum;
  downloadUrl: string | null;
  payload: CreditNoteDto;
}

export class CreditNoteProvider extends AbstractDataProvider<CreditNote> {
  async getOne(resource: string, { id }: GetOneParams): Promise<GetOneResult<CreditNote>> {
    if (resource === "b2cCreditNotes" || resource === "b2bCreditNotes") {
      const backendClient = await getAuthenticatedBackendClient();
      const creditNote = await backendClient.creditNote.creditNoteRetrieve({ id });
      return { data: creditNoteDtoToCreditNote(creditNote) };
    }
    throw new Error("Not implemented");
  }

  async getMany(resource: string, { ids }: GetManyParams): Promise<GetManyResult<CreditNote>> {
    const data: Array<CreditNote> = [];
    for (const id of ids) {
      const { data: record } = await this.getOne(resource, { id });
      data.push(record);
    }
    return { data };
  }

  async getList(resource: string, { pagination, sort, filter }: GetListParams): Promise<GetListResult<CreditNote>> {
    return await fetchCreditNotes(filter, pagination, sort);
  }

  async getManyReference(
    resource: string,
    { id, target, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<CreditNote>> {
    if (resource === "b2bCreditNotes") {
      if (target !== "drivingSchoolId") {
        throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
      }
      filter = { ...filter, drivingSchoolId: id };
    } else if (resource === "b2cCreditNotes") {
      if (target !== "studentId") {
        throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "studentId"`);
      }
      filter = { ...filter, studentId: id };
    }
    return this.getList(resource, { filter, sort, pagination });
  }
}

export const creditNotesProvider = new CreditNoteProvider() as DataProvider;

async function fetchCreditNotes(
  {
    drivingSchoolId,
    studentId,
    recipientId,
  }: {
    drivingSchoolId?: string;
    studentId?: string;
    recipientId?: string;
  },
  pagination: PaginationPayload,
  sort: SortPayload,
): Promise<GetListResult<CreditNote>> {
  const ordering = reactAdminToDRFOrdering(sort);
  const providedFilterCount = [drivingSchoolId, studentId, recipientId].filter(Boolean).length;
  if (providedFilterCount > 1) {
    throw new Error("Es darf nur einer der Filter drivingSchoolId, studentId oder recipientId angegeben werden");
  }
  if (providedFilterCount === 0) {
    throw new Error("Mindestens einer der Filter drivingSchoolId, studentId oder recipientId muss angegeben werden");
  }

  let filter: Parameters<CreditNoteEndpoint["creditNoteList"]>[0];
  if (drivingSchoolId) {
    filter = { recipientCompanyDrivingSchoolId: drivingSchoolId, type: "B2B" };
  } else if (recipientId) {
    filter = { recipientId };
  } else {
    filter = { recipientFirebaseAuthUsersId: studentId };
  }

  const backendClient = await getAuthenticatedBackendClient();
  const { count, results, previous, next } = await backendClient.creditNote.creditNoteList({
    ...reactAdminToDRFPagination(pagination),
    ...filter,
    ordering,
  });

  return {
    data: results.map(creditNoteDtoToCreditNote),
    total: count,
    pageInfo: { hasPreviousPage: !!previous, hasNextPage: !!next },
  };
}

function creditNoteDtoToCreditNote(creditNoteDto: CreditNoteDto): CreditNote {
  const { id, calculation_number, type, created_at, pdf, total_gross, state } = creditNoteDto;
  let localizedType: string = type;
  if (type === "BASE_FEE") {
    localizedType = "für Grundbetrag";
  } else if (type === "TEACHING_MATERIAL") {
    localizedType = "für Lehrmittel";
  } else if (type === "DRIVING_LESSON_INVOICE") {
    localizedType = "für Fahrstunde";
  } else if (type === "REMOTE_LESSON") {
    localizedType = "für Zoom-Meeting";
  } else if (type === "AUTHORITY_FEE") {
    localizedType = "für Prüfungsgebühr";
  } else if (type === "CREDIT_NOTE") {
    localizedType = "Erstattung";
  } else if (type === "PREPAID_CREDITS") {
    localizedType = "Vorauszahlung";
  }
  return {
    id,
    nr: calculation_number ?? id ?? "???",
    type: localizedType,
    createdAt: DateTime.fromISO(created_at),
    total: total_gross.amount ? parseFloat(total_gross.amount) * 100 : undefined,
    status: state,
    downloadUrl: pdf,
    payload: creditNoteDto,
    accuBalance: 0,
  };
}
